import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import AdminLayout from "./components/Layout/AdminLayout";
import EmployeeLayout from "./components/Layout/Employee/EmployeeLayout";
import routes from "./components/Routes/Routes";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import Login from "./components/Pages/Auth/Login";
import SetPassword from "./components/Pages/Auth/SetPassword";
import Unauthorized from "./components/Pages/Auth/Unauthorized";
import { ROLES } from "./constants";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantThemeProvider } from "./contexts/TenantThemeContext";
import { FilterProvider } from "./contexts/FilterContext";

const Careers = lazy(() => import("./components/Pages/PeoplePlus/Careers"));
const CareersApply = lazy(() => import("./components/Pages/PeoplePlus/CareersApply"));
const LandingPage = lazy(() => import("./components/Pages/Landing/LandingPage"));
const EmployeePortalDashboard = lazy(() => import("./components/Pages/Employee/EmployeePortalDashboard"));
const EmployeeDirectory = lazy(() => import("./components/Pages/Employee/EmployeeDirectory"));
const EmployeeProfilePage = lazy(() => import("./components/Pages/Employee/EmployeeProfilePage"));
const EmployeeAttendancePage = lazy(() => import("./components/Pages/Employee/EmployeeAttendancePage"));
const EmployeePoliciesPage = lazy(() => import("./components/Pages/Employee/EmployeePoliciesPage"));
const EmployeeDocumentsPage = lazy(() => import("./components/Pages/Employee/EmployeeDocumentsPage"));

const EmployeeLeavesPage = lazy(() => import("./components/Pages/Employee/EmployeeLeavesPage"));
const EmployeePayslipsPage = lazy(() => import("./components/Pages/Employee/EmployeePayslipsPage"));
const EmployeeReimbursementsPage = lazy(() => import("./components/Pages/Employee/EmployeeReimbursementsPage"));
const EmployeeHolidaysPage = lazy(() => import("./components/Pages/Employee/EmployeeHolidaysPage"));
const EmployeeHelpdeskPage = lazy(() => import("./components/Pages/Employee/EmployeeHelpdeskPage"));
const EmployeeTimesheetPage = lazy(() => import("./components/Pages/Employee/EmployeeTimesheetPage"));
const EmployeeTimesheetForm = lazy(() => import("./components/Pages/Employee/EmployeeTimesheetForm"));
const EmployeeApprovalsPage = lazy(() => import("./components/Pages/Employee/EmployeeApprovalsPage"));

// Register all community features
ModuleRegistry.registerModules([AllCommunityModule]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: "legacy" });

const renderRoutes = (routeList: any[]) => {
  return routeList.map((route) => {
    if (route.children) {
      return (
        <Route key={route.key} path={route.path}>
          {renderRoutes(route.children)}
        </Route>
      );
    }

    return (
      <Route
        key={route.key}
        path={route.path}
        element={
          <Suspense fallback={<div>Loading...</div>}>
            {route.protected ? (
              <ProtectedRoute
                requiredRoles={route.requiredRoles}
                requiredPermissions={route.requiredPermissions}
              >
                {route.component ? (
                  <route.component />
                ) : (
                  <Navigate to="/dashboard" />
                )}
              </ProtectedRoute>
            ) : route.component ? (
              <route.component />
            ) : (
              <Navigate to="/dashboard" />
            )}
          </Suspense>
        }
      />
    );
  });
};

/**
 * Helper: Checks if the current user is ONLY an employee
 * (has EMPLOYEE role but NOT any admin/manager roles)
 */
const RoleBasedRedirect: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const normalizedRoles = (user?.roles || []).map((r: string) =>
    r.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  );

  // Determine if user has any administrative or management permissions
  const hasAdminPermissions = user?.permissions?.some((p: string) => {
    const pUpper = p?.toUpperCase() || '';
    return !pUpper.startsWith('SELF_') && 
      (pUpper.includes('MANAGE') || pUpper.includes('PROCESS') || pUpper.includes('APPROVE') || pUpper.includes('UPDATE') || pUpper.includes('DELETE') || pUpper.includes('VIEW') || pUpper.includes('READ'));
  });
  
  // If they only have EMPLOYEE role and no elevated permissions, redirect to employee portal
  const isOnlyEmployee = normalizedRoles.includes(ROLES.EMPLOYEE) && !hasAdminPermissions && !normalizedRoles.includes(ROLES.SUPER_ADMIN) && !normalizedRoles.includes(ROLES.TENANT_ADMIN);

  if (isOnlyEmployee) {
    return <Navigate to="/employee" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

/**
 * Guard: Redirects EMPLOYEE-only users away from admin routes to /employee
 */
const AdminRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const normalizedRoles = (user?.roles || []).map((r: string) =>
    r.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
  );

  // Determine if user has any administrative or management permissions
  const hasAdminPermissions = user?.permissions?.some((p: string) => {
    const pUpper = p?.toUpperCase() || '';
    return !pUpper.startsWith('SELF_') && 
      (pUpper.includes('MANAGE') || pUpper.includes('PROCESS') || pUpper.includes('APPROVE') || pUpper.includes('UPDATE') || pUpper.includes('DELETE') || pUpper.includes('VIEW') || pUpper.includes('READ'));
  });
  
  // If they only have EMPLOYEE role and no elevated permissions, redirect to employee portal
  const isOnlyEmployee = normalizedRoles.includes(ROLES.EMPLOYEE) && !hasAdminPermissions && !normalizedRoles.includes(ROLES.SUPER_ADMIN) && !normalizedRoles.includes(ROLES.TENANT_ADMIN);

  if (isOnlyEmployee) {
    return <Navigate to="/employee" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <FilterProvider>
          <TenantThemeProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route
                path="/careers"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Careers />
                  </Suspense>
                }
              />
              <Route
                path="/careers/apply/:id"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <CareersApply />
                  </Suspense>
                }
              />

              {/* Landing Page */}
              <Route path="/" element={
                <Suspense fallback={<div>Loading...</div>}>
                  <LandingPage />
                </Suspense>
              } />

              {/* Smart redirect based on role */}
              <Route path="/home" element={<RoleBasedRedirect />} />

              {/* Employee Portal Routes */}
                            <Route
                path="/employee/directory"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeDirectory />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
                            <Route
                path="/employee/policies"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeePoliciesPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/documents"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeDocumentsPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeePortalDashboard />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/profile"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeProfilePage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/attendance"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeAttendancePage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/leaves"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeLeavesPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/payslips"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeePayslipsPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/reimbursements"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeReimbursementsPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/timesheet"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeTimesheetPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/timesheet/create"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeTimesheetForm />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/approvals"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeApprovalsPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/holidays"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeHolidaysPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/helpdesk"
                element={
                  <ProtectedRoute requiredPermissions={["EMPLOYEE_READ"]}>
                    <EmployeeLayout>
                      <Suspense fallback={<div>Loading...</div>}>
                        <EmployeeHelpdeskPage />
                      </Suspense>
                    </EmployeeLayout>
                  </ProtectedRoute>
                }
              />

              {/* Protected Admin Routes — redirect EMPLOYEE-only users to /employee */}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <AdminRouteGuard>
                      <AdminLayout>
                        <Routes>{renderRoutes(routes)}</Routes>
                      </AdminLayout>
                    </AdminRouteGuard>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </TenantThemeProvider>
        </FilterProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
